/* ===========================
   Napibela — UGC Stories
   Extraido de sections/custom-ugc-stories.liquid. Ver component-ugc.css.
   Cada instancia se resuelve por data-instance-id y busca su propio
   #ugc-modal-<id>, asi que varias pueden convivir en una misma pagina.
   =========================== */

if (!customElements.get('ugc-stories')) {
  class UgcStories extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
      this.modal = document.getElementById('ugc-modal-' + this.sectionId);
      this.slides = [];
      this.activeIndex = 0;
      this.muted = true;
      this.io = null;

      this._loadSlideData();
      this._bindTriggers();
      if (this.modal) this._bindModal();
    }

    _loadSlideData() {
      const scripts = this.querySelectorAll('script[data-ugc-slide]');
      scripts.forEach((s) => {
        try {
          this.slides.push(JSON.parse(s.textContent));
        } catch (e) {
          this.slides.push(null);
        }
      });
    }

    _bindTriggers() {
      this.querySelectorAll('[data-ugc-trigger]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.index, 10) || 0;
          this.openAt(idx);
        });
      });
    }

    _bindModal() {
      this.scroller = this.modal.querySelector('[data-ugc-scroller]');
      this.videos = Array.from(this.modal.querySelectorAll('[data-ugc-video]'));
      this.muteBtn = this.modal.querySelector('[data-ugc-mute]');
      this.iconMuted = this.modal.querySelector('[data-icon-muted]');
      this.iconSound = this.modal.querySelector('[data-icon-sound]');
      this.productHeading = this.modal.querySelector('[data-ugc-product-heading]');
      this.productList = this.modal.querySelector('[data-ugc-product-list]');
      this.productEmpty = this.modal.querySelector('[data-ugc-product-empty]');
      this.productTemplate = this.modal.querySelector('[data-ugc-product-template]');

      this.modal.querySelector('[data-ugc-close]').addEventListener('click', () => this.close());
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.close();
      });
      this.modal.addEventListener('close', () => this._onClose());

      this.muteBtn.addEventListener('click', () => this._toggleMute());

      const prev = this.modal.querySelector('[data-ugc-prev]');
      const next = this.modal.querySelector('[data-ugc-next]');
      prev.addEventListener('click', () => this._scrollBy(-1));
      next.addEventListener('click', () => this._scrollBy(1));

      this.modal.querySelectorAll('[data-ugc-tap]').forEach((tap, i) => {
        tap.addEventListener('click', () => this._togglePlay(i));
      });
    }

    openAt(index) {
      if (!this.modal) return;
      this.activeIndex = index;
      this._prepareVideos();
      if (typeof this.modal.showModal === 'function') {
        this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        const target = this.scroller.children[index];
        if (target) this.scroller.scrollTop = target.offsetTop;
        this._setupObserver();
        this._activate(index);
      });
    }

    close() {
      if (typeof this.modal.close === 'function') this.modal.close();
      else this.modal.removeAttribute('open');
    }

    _onClose() {
      document.body.style.overflow = '';
      this.videos.forEach((v) => {
        v.pause();
        v.currentTime = 0;
      });
      if (this.io) this.io.disconnect();
    }

    _prepareVideos() {
      this.videos.forEach((video, i) => {
        if (video.dataset.ready === '1') return;
        const data = this.slides[i];
        if (!data || !data.video || !data.video.sources || data.video.sources.length === 0) return;
        data.video.sources.forEach((src) => {
          const s = document.createElement('source');
          s.src = src.url;
          if (src.mime_type) s.type = src.mime_type;
          video.appendChild(s);
        });
        video.load();
        video.dataset.ready = '1';
      });
    }

    _setupObserver() {
      if (this.io) this.io.disconnect();
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const idx = this.videos.indexOf(entry.target);
            if (idx === -1) return;
            if (entry.intersectionRatio >= 0.6) {
              this._activate(idx);
            }
          });
        },
        { root: this.scroller, threshold: [0, 0.6, 1] }
      );
      this.videos.forEach((v) => this.io.observe(v));
    }

    _activate(index) {
      this.activeIndex = index;
      this.videos.forEach((v, i) => {
        if (i === index) {
          v.muted = this.muted;
          const playPromise = v.play();
          if (playPromise && playPromise.catch) playPromise.catch(() => {});
        } else {
          v.pause();
          v.currentTime = 0;
        }
      });
      this._renderProduct(index);
    }

    _scrollBy(delta) {
      const next = Math.min(this.videos.length - 1, Math.max(0, this.activeIndex + delta));
      const target = this.scroller.children[next];
      if (target) this.scroller.scrollTo({ top: target.offsetTop, behavior: 'smooth' });
    }

    _togglePlay(i) {
      const v = this.videos[i];
      if (!v) return;
      if (v.paused) v.play().catch(() => {});
      else v.pause();
    }

    _toggleMute() {
      this.muted = !this.muted;
      this.videos.forEach((v) => (v.muted = this.muted));
      if (this.iconMuted && this.iconSound) {
        this.iconMuted.hidden = !this.muted;
        this.iconSound.hidden = this.muted;
      }
      this.muteBtn.setAttribute('aria-label', this.muted ? 'Activar sonido' : 'Silenciar');
    }

    _renderProduct(index) {
      const data = this.slides[index];
      const products = (data && data.products) || [];
      this.productList.innerHTML = '';

      if (products.length === 0) {
        this.productHeading.hidden = true;
        this.productEmpty.hidden = false;
        return;
      }
      this.productHeading.hidden = false;
      this.productEmpty.hidden = true;

      products.forEach((product) => {
        const node = this.productTemplate.content.firstElementChild.cloneNode(true);

        const links = node.querySelectorAll('[data-link]');
        links.forEach((a) => (a.href = product.url));
        const img = node.querySelector('[data-img]');
        img.src = product.image || '';
        img.alt = product.title || '';
        if (links[1]) links[1].textContent = product.title;

        node.querySelector('[data-price]').textContent = product.price || '';
        const compare = node.querySelector('[data-compare]');
        if (product.compare_at_price) {
          compare.textContent = product.compare_at_price;
          compare.hidden = false;
        } else {
          compare.hidden = true;
        }

        const select = node.querySelector('[data-variants]');
        if (product.variants && product.variants.length > 1) {
          product.variants.forEach((v) => {
            const opt = document.createElement('option');
            opt.value = v.id;
            opt.textContent = v.title + (v.available ? '' : ' (Agotado)');
            opt.disabled = !v.available;
            if (v.id === product.default_variant_id) opt.selected = true;
            select.appendChild(opt);
          });
          select.hidden = false;
        }

        const addBtn = node.querySelector('[data-add]');
        const label = node.querySelector('[data-add-label]');
        const bloqueo = UgcStories.preventaBloqueo(product);
        if (bloqueo) {
          addBtn.disabled = true;
          label.textContent = bloqueo;
          if (select) select.disabled = true;
        } else if (!product.available) {
          addBtn.disabled = true;
          label.textContent = 'Agotado';
        } else if (product.preventa && product.preventa.es_preventa && product.preventa.estado === 'activa') {
          // es_preventa manda: un producto que salio de preventa puede conservar
          // el estado 'activa' en su metafield y no debe anunciarse como preventa.
          label.textContent = 'Comprar en preventa';
        }
        addBtn.addEventListener('click', () => this._addToCart(node, product));

        this.productList.appendChild(node);
      });
    }

    /*
     * Estados de preventa que NO deben poder agregar al carro. El mismo
     * criterio que usa la ficha (snippets/buy-buttons.liquid): solo 'activa'
     * permite comprar. Devuelve la etiqueta a mostrar, o null si se permite.
     *
     * Ojo: esto es una barrera de interfaz, igual que la de la ficha. El freno
     * duro es el inventario en 0 con "no seguir vendiendo sin existencias",
     * que hace que Shopify rechace el /cart/add.js.
     */
    static preventaBloqueo(product) {
      const pv = product && product.preventa;
      if (!pv || !pv.es_preventa) return null;
      if (pv.estado === 'proximamente') return 'Preventa próximamente';
      if (pv.estado === 'agotada') return 'Preventa agotada';
      if (pv.estado === 'finalizada') return 'Preventa finalizada';
      return null;
    }

    async _addToCart(card, product) {
      if (UgcStories.preventaBloqueo(product)) return;
      const select = card.querySelector('[data-variants]');
      const variantId =
        select && !select.hidden && select.value
          ? parseInt(select.value, 10)
          : product.default_variant_id;
      if (!variantId) return;

      const addBtn = card.querySelector('[data-add]');
      const label = card.querySelector('[data-add-label]');
      const feedback = card.querySelector('[data-feedback]');
      const originalLabel = label.textContent;
      addBtn.disabled = true;
      label.textContent = 'Agregando…';
      feedback.textContent = '';
      feedback.classList.remove('is-error');

      try {
        const body = new FormData();
        body.append('id', variantId);
        body.append('quantity', '1');
        const sectionsToBundle = this._cartSections();
        if (sectionsToBundle) body.append('sections', sectionsToBundle);

        const res = await fetch(this.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body,
        });
        let json = await res.json().catch(() => null);
        // Respaldo Napibela: ver napibelaCompletarSecciones en global.js.
        if (res.ok && sectionsToBundle && typeof napibelaCompletarSecciones === 'function') {
          json = await napibelaCompletarSecciones(json, sectionsToBundle);
        }
        if (!res.ok) {
          console.error('UGC add-to-cart failed:', res.status, json);
          const msg = (json && (json.description || json.message)) || 'No pudimos agregarlo.';
          throw new Error(msg);
        }
        feedback.textContent = '¡Agregado al carro!';
        label.textContent = '¡Agregado!';
        /*
         * Cierra el video ANTES de refrescar el carro. El cart-drawer de Dawn
         * se abre solo al final de renderContents(), y mientras el <dialog>
         * sigue en el top layer el carro queda tapado detras: al cerrar el
         * video reaparecia abierto y descuadrado (encima, _onClose limpia
         * document.body.style.overflow y le borra al drawer su bloqueo de
         * scroll).
         *
         * Solo se cierra si hay drawer o notificacion que mostrar: si el tema
         * unicamente actualiza la burbuja del carro, es mejor dejar el video
         * abierto con su mensaje de confirmacion.
         */
        const drawer = document.querySelector('cart-drawer');
        const notification = document.querySelector('cart-notification');
        const hayCarroVisible = !!(drawer?.renderContents || notification?.renderContents);
        if (hayCarroVisible && json && json.sections) this.close();
        this._refreshCart(json);
        setTimeout(() => {
          label.textContent = originalLabel;
          addBtn.disabled = false;
        }, 1500);
      } catch (e) {
        feedback.textContent = e.message || 'No pudimos agregarlo. Inténtalo de nuevo.';
        feedback.classList.add('is-error');
        label.textContent = originalLabel;
        addBtn.disabled = false;
      }
    }

    _cartSections() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.getSectionsToRender) {
        return cartDrawer
          .getSectionsToRender()
          .map((s) => s.section || s.id)
          .join(',');
      }
      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && cartNotification.getSectionsToRender) {
        return cartNotification
          .getSectionsToRender()
          .map((s) => s.section || s.id)
          .join(',');
      }
      return 'cart-icon-bubble';
    }

    _refreshCart(addResponse) {
      if (!addResponse || !addResponse.sections) {
        this._updateCartBubble(null);
        return;
      }
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.renderContents) {
        /*
         * renderContents() de Dawn quita is-empty de .drawer__inner, pero NO
         * del host <cart-drawer>. Si el carro estaba vacio la clase queda
         * pegada y open() entra por la rama de "carro vacio": busca
         * .drawer__inner-empty, que ya no existe, y le pasa null a trapFocus
         * -> "Cannot read properties of null (reading 'querySelectorAll')".
         * Ademas el CSS de .is-empty deja ocultos los items y el checkout, asi
         * que el drawer se abria mostrando solo las recomendaciones.
         *
         * El camino normal de la ficha hace esta misma limpieza en
         * product-form.js:104; aqui faltaba. Por eso el bug solo aparecia con
         * el carro vacio, no segun la pagina.
         */
        if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(addResponse);
        return;
      }
      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && cartNotification.renderContents) {
        cartNotification.renderContents(addResponse);
        return;
      }
      this._updateCartBubble(addResponse.sections['cart-icon-bubble']);
    }

    _updateCartBubble(html) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble || !html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('.shopify-section') || parsed.body;
      if (fresh) bubble.innerHTML = fresh.innerHTML;
    }

  }

  customElements.define('ugc-stories', UgcStories);
}
